import React, { useContext, useState, useEffect, useRef } from 'react';
import { Popover } from '@headlessui/react';
import { AuthContext } from "../contexts/auth";
import Footer from "../components/footer";
import { importEmployees, downloadTemplate, fetchImportInstructions, downloadReferenceData } from '../services/api_import_employees';
import Swal from 'sweetalert2';
import { Link } from 'wouter';
import { estraiMessaggiErrore } from '../services/utils';

const downloadBase64File = (mime, base64, filename) => {
  const link = document.createElement('a');
  link.href = `data:${mime};base64,${base64}`;
  link.download = filename || 'errori.csv';
  document.body.appendChild(link);
  link.click();
  link.remove();
};


export default function UploadCSVEmployee() {
    const { profile, token } = useContext(AuthContext);
    const [selectedFile, setSelectedFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [instructions, setInstructions] = useState({});
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);

    const ALLOWED_EXTENSIONS = [".csv", ".xlsx"];

    const handleFileChange = (event) => {
        const file = event.target.files?.[0] ?? null;

        if (!file) {
            setSelectedFile(null);
            return;
        }

        const lowerName = file.name.toLowerCase();
        const isSupported = ALLOWED_EXTENSIONS.some((extension) =>
            lowerName.endsWith(extension)
        );

        if (!isSupported) {
            Swal.fire({
                icon: "warning",
                title: "Formato non supportato",
                text: "Carica un file in formato .xlsx oppure .csv.",
            });

            event.target.value = "";
            setSelectedFile(null);
            return;
        }

        setSelectedFile(file);
    };

    const handleImport = async () => {
        if (!selectedFile) {
            Swal.fire({
                icon: "warning",
                title: "Nessun file selezionato",
                text: "Seleziona un file Excel o CSV prima di confermare",
            });
            return;
        }

        setLoading(true);

        try {
            const result = await importEmployees(
                token,
                selectedFile,
                profile.profile.id,
                "payrolls"
            );

            const imported = result?.imported ?? 0;
            const requestedApp = result?.requested_app ?? 0;
            const activatedApp = result?.activated_app ?? 0;
            const skippedApp = result?.skipped_app ?? 0;
            const errorsCount = result?.errors_count ?? 0;

            const hasErrors = errorsCount > 0;
            const hasSkippedActivations = skippedApp > 0;

            const icon = hasErrors
                ? "error"
                : hasSkippedActivations
                    ? "warning"
                    : "success";

            const title = hasErrors
                ? "Importazione completata con errori"
                : hasSkippedActivations
                    ? "Importazione completata con avvisi"
                    : "Importazione completata!";

            const msg = `
                <div style="text-align: left; display: inline-block;">
                    <p>Righe importate: ${imported}</p>
                    <p>Attivazioni richieste: ${requestedApp}</p>
                    <p>Attivate: ${activatedApp}</p>
                    <p>Non attivate: ${skippedApp}</p>
                    ${
                        hasErrors
                            ? `<p>Righe con errori: ${errorsCount}</p>`
                            : ""
                    }
                </div>
            `;

            const { isDenied } = await Swal.fire({
                icon,
                title,
                html: msg,
                allowOutsideClick: false,
                confirmButtonText: "OK",
                showDenyButton: Boolean(result?.errors_file_b64),
                denyButtonText: "Scarica file errori",
            });

            if (isDenied && result?.errors_file_b64) {
                downloadBase64File(
                    "text/csv",
                    result.errors_file_b64,
                    result.errors_filename || "errori.csv"
                );
            }
        } catch (err) {
            const msg = estraiMessaggiErrore(err);

            Swal.fire({
                icon: "error",
                title: "Errore durante l'importazione",
                html: msg,
                confirmButtonText: "OK",
                allowOutsideClick: false,
            });
        } finally {
            setLoading(false);
            setSelectedFile(null);

            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    };

    useEffect(() => {
        if (!selectedFile && fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, [selectedFile]);

    const handleDownloadTemplate = async (format) => {
        try {
            await downloadTemplate(token, format);
        } catch (error) {
            console.error("Errore nel download del template:", error);

            const message = estraiMessaggiErrore(error);

            Swal.fire({
                icon: "error",
                title: "Errore download",
                text: message,
                confirmButtonText: "OK",
            });
        }
    };

    useEffect(() => {
        (async () => {
            try {
                const data = await fetchImportInstructions(token, 'employee', 'payrolls');
                setInstructions(data || {});
            } catch (e) {
                setError(e?.message || 'Errore nel caricamento delle istruzioni');
            }
        })();
    }, [token]);

    return (
        <div className="flex flex-col min-h-screen">
            <Popover as="header" className="bg-indigo-200">
                <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:max-w-7xl lg:px-8">
                    <div className="relative flex flex-wrap items-center justify-center lg:justify-between">
                        <div className="w-full -5 lg:border-t lg:border-white lg:border-opacity-20">
                            <div className="flex justify-center my-2 py-2">
                                <h2 className="text-center text-2xl font-semibold text-indigo-800">
                                Importa Dipendenti
                                </h2>
                            </div>
                        </div>
                    </div>
                </div>
            </Popover>

            <div className="flex items-center justify-between my-4 px-4 sm:px-6 lg:max-w-7xl lg:px-8 pt-8">
                <button
                    className="border block rounded-md bg-grey-400 px-3 py-2 mr-2 text-center text-sm font-semibold text-grey-800 shadow-sm hover:bg-grey-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-grey-600 hover:bg-gray-100"
                    title="Back"
                >
                <Link to={`/nuovo_dipendente`} className="flex items-center">
                    Torna indietro
                </Link>
                </button>
            </div>

            <main className="flex-grow pb-8">
                <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:max-w-7xl lg:px-8">
                    <h1 className="text-xl font-bold my-4 text-indigo-700">Carica il tuo file Excel o CSV</h1>

                    <input
                        type="file"
                        accept=".csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                        ref={fileInputRef}
                        className="block file:py-6 w-full border border-gray-200 rounded-md shadow-md mb-4 text-sm text-slate-500
                                file:mr-4 file:px-4
                                file:rounded-75 file:border-0
                                file:text-sm file:font-semibold
                                file:bg-indigo-50 file:text-indigo-700
                                hover:file:bg-indigo-100"
                        onChange={handleFileChange}
                    />

                    <div className="mb-5">
                        <p className="mb-2 text-sm font-normal text-gray-800">
                            Scarica il template di esempio nel formato che preferisci:
                        </p>

                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={() => handleDownloadTemplate("xlsx")}
                                className="inline-flex items-center justify-center rounded-md text-xs font-bold px-2.5 h-8 border bg-indigo-50 border-indigo-500 text-indigo-700 hover:bg-indigo-200"
                            >
                                Scarica Excel
                            </button>

                            <button
                                type="button"
                                onClick={() => handleDownloadTemplate("csv")}
                                className="inline-flex items-center justify-center rounded-md text-xs font-bold px-2.5 h-8 border bg-indigo-50 border-indigo-500 text-indigo-700 hover:bg-indigo-200"
                            >
                                Scarica CSV
                            </button>
                        </div>
                    </div>

                    <button
                        onClick={handleImport}
                        className="flex w-40 items-center justify-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 mx-auto"
                        disabled={loading}
                    >
                        {loading ? 'Importando...' : 'Conferma'}
                    </button>

                    <hr className="my-6" />
                    <p className="text-lg font-bold my-4 text-indigo-700">
                        Segui le istruzioni per la compilazione del file Excel o CSV
                    </p>
                </div>

                <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:max-w-7xl lg:px-8">
                    {error && <p className="text-red-700 text-sm mb-2">{error}</p>}
                    <table className="min-w-full">
                        <thead className="bg-indigo-50 text-indigo-800">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider rounded-l-lg">
                                Colonna
                                </th>
                                <th className=" py-3 text-left text-xs font-bold uppercase tracking-wider rounded-r-lg">
                                Tipo
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {instructions &&
                                Object.keys(instructions).map((fieldKey) => {
                                const field = instructions[fieldKey];
                                return (
                                    <tr key={fieldKey}>
                                        <td className="p-1 pl-4 text-sm text-gray-800">{field.name}</td>
                                        <td className="p-1 text-sm text-gray-800">
                                            <div className="flex items-center gap-3">
                                                <span dangerouslySetInnerHTML={{ __html: field.type_text || 'N/D' }}></span>
                                                {field.has_dataset && field.download && (
                                                    <button
                                                        type="button"
                                                        className="rounded-md bg-indigo-50 px-2 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
                                                        onClick={() =>
                                                            downloadReferenceData(token, field.download.entity, field.download.field)
                                                        }
                                                        title={`Scarica dati per ${field.name}`}
                                                    >
                                                        Scarica
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </main>
            <Footer />
        </div>
    );
}
